import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { LineChart as LineChartIcon, TrendingUp, Utensils, Pill, Activity, Download, Info, Calendar } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceLine } from "recharts";
import { useSiteTime } from "../lib/time";

const TEAL = "#0D9488";
const TEAL_L = "#5EEAD4";
const SLATE = "#0F172A";
const MUTED = "#64748B";

const RANGES = [
  { key: "30",  label: "30 days" },
  { key: "60",  label: "60 days" },
  { key: "90",  label: "90 days" },
  { key: "180", label: "6 months" },
];

// ---------------- Small building blocks (kept in-file so the page ships as a single unit) ----------------
function LegendChip({ color, label, hint }) {
  return (
    <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 font-mono-num">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
      <b className="text-slate-800 dark:text-slate-100">{label}</b>
      {hint && <span className="text-slate-400 hidden sm:inline">· {hint}</span>}
    </div>
  );
}

function StatTile({ label, value, sub, color }) {
  return (
    <div className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg p-3">
      <div className="text-[10px] font-mono-num uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5" style={{ color: color || SLATE }}>{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// Compact lane that renders event dots (shots) or bars (cycles) along the same date axis.
function EventLane({ title, items, color, renderItem, empty }) {
  return (
    <div className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg p-3 sm:p-4" data-testid={`timeline-lane-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
        <div className="text-[11px] uppercase tracking-widest font-mono-num text-slate-500 dark:text-slate-400">{title}</div>
        <div className="text-[10px] text-slate-400 ml-auto">{items?.length || 0}</div>
      </div>
      {items?.length ? (
        <div className="flex flex-wrap gap-1.5">{items.map((it, i) => renderItem(it, i))}</div>
      ) : (
        <div className="text-[11px] text-slate-400 dark:text-slate-500 italic py-1">{empty || "No entries in this range."}</div>
      )}
    </div>
  );
}

// ---------------- Main page ----------------
export default function InsightsTimeline() {
  const { fmtDate } = useSiteTime();
  const [rangeKey, setRangeKey] = useState("90");
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = cycleId ? { cycle_id: cycleId } : { days: rangeKey };
      const { data: d } = await api.get("/insights/timeline", { params });
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [rangeKey, cycleId]);
  useEffect(() => { load(); }, [load]);

  // Fetch cycles for the picker.
  useEffect(() => {
    api.get("/cycles").then(({ data: d }) => setCycles(Array.isArray(d) ? d : (d?.cycles || d?.rows || [])))
      .catch(() => setCycles([]));
  }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (cycleId) params.set("cycle_id", cycleId); else params.set("days", rangeKey);
      const resp = await api.get(`/insights/timeline/pdf?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `amino-timeline-${rangeKey}d.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Timeline PDF downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloading(false);
    }
  };

  const weightSeries = useMemo(
    () => (data?.weights || []).map((w) => ({ x: new Date(w.logged_at).getTime(), weight: w.weight, unit: w.unit })),
    [data]
  );
  const nutritionSeries = useMemo(
    () => (data?.nutrition?.days || []).map((d) => ({ date: d.date, calories: d.calories, protein_g: d.protein_g })),
    [data]
  );

  const isEmpty = data && data.summary.shots_total === 0 && data.weights.length === 0 && (data.nutrition?.days_logged || 0) === 0;

  return (
    <AppLayout>
      <PageHero
        eyebrow="Insights"
        title="Protocol Outcome Timeline"
        subtitle="A private, side-by-side view of your logged shots, cycles, weight, nutrition, and supplements. Observational only — not medical advice."
        icon={<LineChartIcon className="w-8 h-8" style={{ color: TEAL }} />}
      />

      {/* Controls: range + cycle picker + PDF */}
      <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl p-4 mb-4" data-testid="timeline-controls">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Timeline range">
            {RANGES.map(({ key, label }) => {
              const active = !cycleId && rangeKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setCycleId(""); setRangeKey(key); }}
                  data-testid={`timeline-range-${key}`}
                  className={`h-9 px-3 rounded-full text-xs font-mono-num uppercase tracking-widest border transition-colors ${
                    active
                      ? "bg-[#0D9488] border-[#0D9488] text-white"
                      : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-teal-300"
                  }`}
                >
                  {label}
                </button>
              );
            })}
            {cycles.length > 0 && (
              <div className="flex items-center gap-1.5 ml-1">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <select
                  value={cycleId}
                  onChange={(e) => setCycleId(e.target.value)}
                  className="h-9 px-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-mono-num text-slate-700 dark:text-slate-300"
                  data-testid="timeline-cycle-select"
                >
                  <option value="">Or pick a cycle…</option>
                  {cycles.map((c) => (
                    <option key={c.id || c._id} value={c.id || c._id}>
                      {c.peptide_name || c.name} — {c.start_date?.slice(0, 10)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <Button
            type="button"
            onClick={downloadPdf}
            disabled={downloading || !data}
            className="bg-[#0D9488] hover:bg-[#0F766E] text-white h-9 px-4 rounded-md font-semibold uppercase tracking-widest text-xs"
            data-testid="timeline-download-pdf"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            {downloading ? "Preparing…" : "PDF for doctor visit"}
          </Button>
        </div>
        {data && (
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-3 font-mono-num" data-testid="timeline-range-label">
            {data.range.label} · {data.range.start.slice(0, 10)} → {data.range.end.slice(0, 10)}
          </div>
        )}
      </Card>

      {loading ? (
        <Card className="p-6 text-center text-slate-500 dark:text-slate-400 text-sm">Loading timeline…</Card>
      ) : isEmpty ? (
        <Card className="p-8 text-center border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-900" data-testid="timeline-empty">
          <LineChartIcon className="w-10 h-10 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Not enough entries yet</div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto leading-relaxed">
            Log a few shots, weigh-ins, or meals and check back — the timeline stitches everything together automatically.
          </div>
        </Card>
      ) : data && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="timeline-summary">
            <StatTile label="Shots" value={data.summary.shots_total} sub={`${data.summary.days_active} active days`} color={TEAL} />
            <StatTile label="Peptides" value={data.summary.peptides_logged.length} sub={data.summary.peptides_logged.slice(0, 2).join(" · ") || "—"} />
            <StatTile
              label="Weight change"
              value={data.summary.weight_delta ? `${data.summary.weight_delta.delta >= 0 ? "+" : ""}${data.summary.weight_delta.delta} ${data.summary.weight_delta.unit}` : "—"}
              sub={data.summary.weight_delta ? `${data.summary.weight_delta.first} → ${data.summary.weight_delta.last}` : "Log twice to see delta"}
              color={data.summary.weight_delta?.delta < 0 ? "#0D9488" : (data.summary.weight_delta?.delta > 0 ? "#DC2626" : SLATE)}
            />
            <StatTile
              label="Meal-logging"
              value={data.nutrition.days_logged_pct != null ? `${data.nutrition.days_logged_pct}%` : "—"}
              sub={`${data.nutrition.days_logged} of ${data.nutrition.total_days} days`}
              color="#F59E0B"
            />
          </div>

          {/* Legend */}
          <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl p-3 mb-4 flex flex-wrap gap-x-4 gap-y-2" data-testid="timeline-legend">
            <LegendChip color={TEAL}     label="Shots"       hint="Each dot = one logged shot" />
            <LegendChip color="#3B82F6"  label="Cycles"      hint="Peptide protocols with start/end dates" />
            <LegendChip color="#10B981"  label="Weight"      hint="Trend line, first → last in range" />
            <LegendChip color="#F59E0B"  label="Nutrition"   hint="Days you logged meals" />
            <LegendChip color="#8B5CF6"  label="Supplements" hint="% of days all supplements taken" />
          </Card>

          {/* Weight trend chart */}
          <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl p-4 mb-4" data-testid="timeline-weight-chart">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <div className="text-[11px] uppercase tracking-widest font-mono-num text-slate-500 dark:text-slate-400">Weight trend</div>
              <div className="text-[10px] text-slate-400 ml-auto">{data.weights.length} logs</div>
            </div>
            {weightSeries.length >= 2 ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={weightSeries} margin={{ top: 8, right: 10, bottom: 0, left: -10 }}>
                  <XAxis dataKey="x" type="number" domain={["dataMin", "dataMax"]} tick={{ fontSize: 10, fill: MUTED }} tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" })} />
                  <YAxis dataKey="weight" tick={{ fontSize: 10, fill: MUTED }} domain={["dataMin - 2", "dataMax + 2"]} width={40} />
                  <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} formatter={(v) => [`${v} ${weightSeries[0]?.unit || "lb"}`, "Weight"]} />
                  <Line type="monotone" dataKey="weight" stroke="#10B981" strokeWidth={2} dot={{ r: 3, fill: "#10B981" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-[11px] text-slate-400 italic py-6 text-center">Log at least two weigh-ins to see the trend line.</div>
            )}
          </Card>

          {/* Nutrition daily calories */}
          <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl p-4 mb-4" data-testid="timeline-nutrition-chart">
            <div className="flex items-center gap-2 mb-3">
              <Utensils className="w-4 h-4 text-amber-600" />
              <div className="text-[11px] uppercase tracking-widest font-mono-num text-slate-500 dark:text-slate-400">Daily calories</div>
              {data.nutrition.calorie_goal ? (
                <div className="text-[10px] text-slate-400 ml-auto">Goal: {data.nutrition.calorie_goal} kcal</div>
              ) : null}
            </div>
            {nutritionSeries.length >= 2 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={nutritionSeries} margin={{ top: 8, right: 10, bottom: 0, left: -10 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: MUTED }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 10, fill: MUTED }} width={40} />
                  {data.nutrition.calorie_goal ? (
                    <ReferenceLine y={data.nutrition.calorie_goal} stroke="#F59E0B" strokeDasharray="3 3" />
                  ) : null}
                  <Tooltip />
                  <Line type="monotone" dataKey="calories" stroke="#F59E0B" strokeWidth={2} dot={{ r: 2, fill: "#F59E0B" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-[11px] text-slate-400 italic py-6 text-center">Log meals on at least two days to see the trend.</div>
            )}
            <div className="grid grid-cols-3 gap-2 mt-3">
              <StatTile label="Avg calories" value={data.nutrition.avg_calories || "—"} />
              <StatTile label="Avg protein" value={data.nutrition.avg_protein_g ? `${data.nutrition.avg_protein_g}g` : "—"} />
              <StatTile label="Days logged" value={`${data.nutrition.days_logged_pct}%`} sub={`${data.nutrition.days_logged}/${data.nutrition.total_days}`} />
            </div>
          </Card>

          {/* Supplement completion */}
          <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl p-4 mb-4" data-testid="timeline-supplements">
            <div className="flex items-center gap-2 mb-3">
              <Pill className="w-4 h-4 text-purple-600" />
              <div className="text-[11px] uppercase tracking-widest font-mono-num text-slate-500 dark:text-slate-400">Supplement completion</div>
            </div>
            {data.supplements.active_supplement_count > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                <StatTile label="Active supplements" value={data.supplements.active_supplement_count} />
                <StatTile label="All-taken days" value={data.supplements.days_all_taken} />
                <StatTile label="Completion" value={data.supplements.completion_pct != null ? `${data.supplements.completion_pct}%` : "—"} color="#8B5CF6" />
              </div>
            ) : (
              <div className="text-[11px] text-slate-400 italic py-2">No active supplements set up.</div>
            )}
          </Card>

          {/* Event lanes: cycles + shots */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
            <EventLane
              title="Cycles in window"
              items={data.cycles}
              color="#3B82F6"
              empty="No cycles overlap this window."
              renderItem={(c, i) => (
                <div key={i} className="px-2.5 py-1 rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800 text-[11px]">
                  <b className="text-slate-900 dark:text-slate-100">{c.peptide_name}</b>
                  <span className="text-slate-500 dark:text-slate-400 ml-1.5">{fmtDate(c.start_date)}{c.end_date ? ` → ${fmtDate(c.end_date)}` : " · active"}</span>
                </div>
              )}
            />
            <EventLane
              title="Shots (latest first)"
              items={[...data.shots].reverse().slice(0, 30)}
              color={TEAL}
              empty="No shots logged in this window."
              renderItem={(s, i) => (
                <div key={i} className="px-2.5 py-1 rounded-md border border-teal-200 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-800 text-[11px]">
                  <b className="text-slate-900 dark:text-slate-100">{s.peptide_name}</b>
                  <span className="text-slate-500 dark:text-slate-400 ml-1.5">{s.dose_amount}{s.dose_unit} · {fmtDate(s.taken_at)}</span>
                </div>
              )}
            />
          </div>

          {/* Disclaimer */}
          <Card className="border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/60 rounded-xl p-4" data-testid="timeline-disclaimer">
            <div className="flex items-start gap-2 text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
              <div>{data.disclaimer}</div>
            </div>
          </Card>
        </>
      )}
    </AppLayout>
  );
}
